import React from "react";
import { Box, Typography, List, ListItem, useTheme } from "@mui/material";
import IntegrationsIcon from "@mui/icons-material/Extension";

/**
 * Reusable filter sidebar component with category filtering
 * @param {Array} categories - Array of {key, title, count} objects
 * @param {String} selectedCategory - Currently selected category key
 * @param {Function} onCategorySelect - Callback when category is selected
 * @param {Number} totalCount - Total count for "All" option
 */
const FilterSidebar = ({
  categories,
  selectedCategory,
  onCategorySelect,
  totalCount,
}) => {
  const theme = useTheme();
  const allKey = "All";

  // Common List Item Component
  const FilterListItem = ({ label, count, isSelected, onClick }) => (
    <ListItem
      onClick={onClick}
      sx={{
        cursor: "pointer",
        py: 1.5,
        borderLeft: isSelected
          ? `4px solid ${theme.palette.primary.main}`
          : "4px solid transparent",
        backgroundColor: isSelected ? "rgba(3, 27, 137, 0.05)" : "transparent",
        "&:hover": {
          backgroundColor: "rgba(3, 27, 137, 0.04)",
        },
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: isSelected ? 600 : 400,
          color: theme.customColors.text.primary,
        }}
      >
        {label}
      </Typography>
      {count > 0 && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "24px",
            height: "24px",
            px: 0.75,
            backgroundColor: isSelected
              ? theme.palette.primary.main
              : theme.customColors.background.dialog,
            color: isSelected ? "white" : theme.customColors.text.secondary,
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {count}
        </Box>
      )}
    </ListItem>
  );

  return (
    <Box
      sx={{
        backgroundColor: theme.customColors.background.main,
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.strong}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "16px",
          backgroundColor: theme.customColors.background.header,
          borderBottom: `1px solid ${theme.customColors.border.strong}`,
        }}
      >
        <IntegrationsIcon
          sx={{
            fontSize: 24,
            color: theme.customColors.icons.secondary,
            marginRight: theme.spacing(0.5),
          }}
        />
        <Typography variant="subtitle1" fontWeight="700">
          Integrations
        </Typography>
      </Box>

      {/* Content Section */}
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        <List sx={{ p: 0 }}>
          {/* All Option */}
          <FilterListItem
            label="All"
            count={totalCount}
            isSelected={selectedCategory === allKey}
            onClick={() => onCategorySelect(allKey)}
          />

          {/* Category List */}
          {categories.map(({ key, title, count }) => (
            <FilterListItem
              key={key}
              label={title}
              count={count}
              isSelected={selectedCategory === key}
              onClick={() => onCategorySelect(key)}
            />
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default FilterSidebar;
